import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Banner} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

import {UPGRADE_PLAN_URL} from '../../utils/copilot-chat-helpers'
import {copilotLocalStorage} from '../../utils/copilot-local-storage'
import styles from './CopilotCodingAgentQuotaBanner.module.css'
import {useEntitlement} from './EntitlementContext'

export interface CopilotCodingAgentQuotaBannerProps {
  /**
   * Callback fired when the quota exhausted state changes.
   * Use this to disable submit buttons or take other actions when quota is exhausted.
   */
  onQuotaExhaustedChange?: (isExhausted: boolean) => void
  entrypointLocation: string
}

/**
 * Hook to determine if the CopilotCodingAgentQuotaBanner should be shown.
 * Use this to conditionally render the banner wrapper to avoid unnecessary DOM elements.
 */
export function useShouldShowCodingAgentQuotaBanner(): boolean {
  const {chatQuotaRemaining, isLicensedLimited, chatQuotaExceeded, resetDate} = useEntitlement()

  const isFreeUserExperimentEnabled = isFeatureEnabled('copilot_coding_agent_free_users_exp')
  const shouldShowBanners = isFreeUserExperimentEnabled && isLicensedLimited

  const [isApproachingBannerDismissed] = useState(() =>
    resetDate ? copilotLocalStorage.getCodingAgentQuotaBannerDismissed(resetDate) : false,
  )

  const showApproachingBanner =
    shouldShowBanners && !chatQuotaExceeded && chatQuotaRemaining <= 50 && !isApproachingBannerDismissed
  const showExhaustedBanner = shouldShowBanners && chatQuotaExceeded

  return showApproachingBanner || showExhaustedBanner
}

/**
 * Displays upgrade banners for free Copilot users based on their quota usage.
 *
 * - Shows an "info" variant dismissible banner when 50-99% of quota is used
 * - Shows an "upsell" variant non-dismissible banner when 100% of quota is used
 */
export function CopilotCodingAgentQuotaBanner({
  onQuotaExhaustedChange,
  entrypointLocation,
}: CopilotCodingAgentQuotaBannerProps) {
  const {chatQuotaRemaining, isLicensedLimited, chatQuotaExceeded, resetDate} = useEntitlement()

  const isFreeUserExperimentEnabled = isFeatureEnabled('copilot_coding_agent_free_users_exp')
  const percentageUsed = 100 - chatQuotaRemaining
  const shouldShowBanners = isFreeUserExperimentEnabled && isLicensedLimited

  const handleUpgradeClick = useCallback(() => {
    sendEvent('copilot_growth.free_cca', {
      target: `QUOTA_UPSELL_CLICK_${entrypointLocation}`,
    })
  }, [entrypointLocation])

  const [isApproachingBannerDismissed, setIsApproachingBannerDismissed] = useState(() =>
    resetDate ? copilotLocalStorage.getCodingAgentQuotaBannerDismissed(resetDate) : false,
  )

  const showApproachingBanner =
    shouldShowBanners && !chatQuotaExceeded && chatQuotaRemaining <= 50 && !isApproachingBannerDismissed
  const showExhaustedBanner = shouldShowBanners && chatQuotaExceeded

  // Notify parent when quota exhausted state changes
  useEffect(() => {
    onQuotaExhaustedChange?.(showExhaustedBanner)
  }, [showExhaustedBanner, onQuotaExhaustedChange])

  const handleDismissApproachingBanner = useCallback(() => {
    if (resetDate) {
      copilotLocalStorage.setCodingAgentQuotaBannerDismissed(resetDate)
      setIsApproachingBannerDismissed(true)
    }
  }, [resetDate])

  if (!showApproachingBanner && !showExhaustedBanner) {
    return null
  }

  const bannerConfig = showExhaustedBanner
    ? {
        variant: 'upsell' as const,
        title: 'Limit reached',
        testId: 'coding-agent-quota-exhausted-banner',
        message: `You have reached your free plan limit. Limit resets on ${resetDate}.`,
        onDismiss: undefined,
      }
    : {
        variant: 'info' as const,
        title: 'Quota approaching',
        testId: 'coding-agent-quota-approaching-banner',
        message: `You've used ${percentageUsed}% of your free requests this month.`,
        onDismiss: handleDismissApproachingBanner,
      }

  return (
    <div className={styles.container} data-testid={bannerConfig.testId}>
      <Banner
        variant={bannerConfig.variant}
        title={bannerConfig.title}
        hideTitle
        primaryAction={
          <Banner.PrimaryAction as="a" href={UPGRADE_PLAN_URL} onClick={handleUpgradeClick}>
            Upgrade To Copilot Pro
          </Banner.PrimaryAction>
        }
        onDismiss={bannerConfig.onDismiss}
      >
        {bannerConfig.message}
      </Banner>
    </div>
  )
}
